// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CoolingDatePickerComponent } from '../cooling-date-picker/cooling-date-picker.component';
import { CoolingGraphDisplayComponent } from '../cooling-graph-display/cooling-graph-display.component';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { SystemStatusComponent } from '../system-status/system-status.component';
import { CoolingCompanionService } from '../../services/cooling-companion.service';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouteService } from 'src/app/modules/portal/services/routeService.service';
import { SettingsService } from 'src/app/modules/shared/services/settings.service';
import localDateFormatsJson from '../../../../config/localDateFormats.json';
import { LocalDateFormat } from 'src/app/_interfaces/data/local-data-format-model';
import {
  ChartYAxisConfig,
  ChartSeriesConfig,
  CoolingChartConfig,
} from '../../interfaces/chart-config.interface';
import { PARAMETER_CONFIG } from '../../config/graph-config';
import {
  SystemStatus,
  SystemStatusCollection,
} from '../../interfaces/system-status.interface';
import { TranslateModule } from '@ngx-translate/core';
import {
  DateFilterFn,
  DateRange,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatNativeDateModule,
} from '@angular/material/core';
import { CustomDateAdapter, PICK_FORMATS } from './custom-date.adaptor';
import { CoolingKeyValueData } from '../../interfaces/cooling-key-value-data.interface';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { GraphState } from '../../interfaces/graph-state.interface';

@Component({
  selector: 'app-cooling-system-details',
  standalone: true,
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PICK_FORMATS },
  ],
  imports: [
    TranslateModule,
    SystemStatusComponent,
    CoolingDatePickerComponent,
    CoolingGraphDisplayComponent,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatSelectModule,
  ],
  templateUrl: './cooling-system-details.component.html',
  styleUrl: './cooling-system-details.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CoolingSystemDetailsComponent implements OnInit {
  private readonly DEFAULT_LOOKBACK_DAYS = 6;
  calendarActiveDays: Record<string, number> | null = {};
  graphStateList: GraphState[] = [];
  readonly parameterOptions = this.excludeThresholdsInGraph(PARAMETER_CONFIG);
  systemStatus: SystemStatus[] = [];
  isSystemStatusLoading = false;
  localDateFormats: LocalDateFormat[] = localDateFormatsJson;
  systemDetails = {
    systemMaterialNumber: 0,
    systemSerialNumber: '',
  };
  selectedDateFormat: LocalDateFormat | undefined;
  latestStatusDate: string | undefined;
  timezoneOffsetInHours: string | undefined;

  constructor(
    private settingsService: SettingsService,
    protected coolingCompanionService: CoolingCompanionService,
    private routeService: RouteService,
    private adapter: DateAdapter<Date>,
  ) {
    const settings = this.settingsService.getSettings();
    const formatId = settings?.localDateFormatId;
    this.selectedDateFormat =
      localDateFormatsJson.find((f) => f.id === formatId) ||
      localDateFormatsJson[0];
    this.settingsService.dateFormat$
      .pipe(takeUntilDestroyed())
      .subscribe((dateformat: LocalDateFormat) => {
        this.selectedDateFormat = dateformat;
        this.adapter.setLocale('en-US');
      });
    this.getSystemDetails();
  }

  private getSystemDetails() {
    this.routeService.currentSystem$
      .pipe(takeUntilDestroyed())
      .subscribe((system) => {
        if (!system) return;
        this.systemDetails = {
          systemSerialNumber: system.systemSerialNumber,
          systemMaterialNumber: system.systemMaterialNumber,
        };
      });
  }

  private toYYYYMMDD(date: Date): string {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  private excludeThresholdsInGraph(PARAMETER_CONFIG: object): string[] {
    const excludedParameters = ['Threshold', 'Threshold Alert'];
    return Object.keys(PARAMETER_CONFIG).filter((key) => {
      return !excludedParameters.includes(key);
    });
  }

  ngOnInit(): void {
    this.getCalendarLogCount();
    this.initializeGraphStatus();
    this.initGraphApi();
    this.getSystemStatus(24);
  }

  //---- global calendar data ----
  getCalendarLogCount(): void {
    this.coolingCompanionService
      .getCalendarLogCount(
        this.systemDetails.systemSerialNumber,
        this.systemDetails.systemMaterialNumber,
      )
      .subscribe({
        next: (calendarActiveDays) => {
          this.calendarActiveDays = calendarActiveDays;
        },
        error: () => {
          this.calendarActiveDays = null;
        },
      });
  }

  //---- graph ----
  initializeGraphStatus(): void {
    const defaultDateRange = this.createDefaultDateRange();

    this.graphStateList = [
      {
        status: 'idle',
        dateRange: defaultDateRange,
        parameterSelections: this.parameterOptions.slice(),
        configList: null,
      },
    ];
  }

  private createDefaultDateRange(): { startDate: string; endDate: string } {
    const today = new Date();
    const dateDaysAgo = new Date();
    dateDaysAgo.setDate(today.getDate() - this.DEFAULT_LOOKBACK_DAYS);

    return {
      startDate: this.toYYYYMMDD(dateDaysAgo),
      endDate: this.toYYYYMMDD(today),
    };
  }

  initGraphApi(): void {
    for (let i = 0; i < this.graphStateList.length; i++) {
      this.getGraphData(
        {
          startDate: this.graphStateList[i].dateRange.startDate,
          endDate: this.graphStateList[i].dateRange.endDate,
        },
        i,
        this.graphStateList[i].parameterSelections,
      );
    }
  }

  rangeFilter: DateFilterFn<Date> = (date: Date | null): boolean => {
    if (!date) {
      return false;
    }
    if (this.calendarActiveDays) {
      const formattedDate = this.toYYYYMMDD(date);
      return this.calendarActiveDays.hasOwnProperty(formattedDate);
    }
    return true;
  };

  getGraphData(
    dateRange: { startDate: string; endDate: string },
    graphIndex: number,
    selectedParameters?: string[],
  ): void {
    const graphRequestBody = {
      materialNumber: this.systemDetails.systemMaterialNumber,
      serialNumber: this.systemDetails.systemSerialNumber,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    this.graphStateList[graphIndex].status = 'loading';

    this.coolingCompanionService
      .getCoolingGraphData(graphRequestBody)
      .subscribe({
        next: (data: CoolingKeyValueData) => {
          this.graphStateList[graphIndex].configList =
            this.getGraphConfigSettings(data, selectedParameters);
          this.graphStateList[graphIndex] = {
            ...this.graphStateList[graphIndex],
            status: 'loaded',
          };
        },
        error: () => {
          this.graphStateList[graphIndex] = {
            ...this.graphStateList[graphIndex],
            status: 'error',
          };
        },
      });
  }

  onDateChange(graphIdx: number, dateRangeInput: DateRange<Date> | null): void {
    if (!dateRangeInput || !dateRangeInput.start || !dateRangeInput.end) {
      return;
    }
    const start = this.toYYYYMMDD(dateRangeInput.start);
    const end = this.toYYYYMMDD(dateRangeInput.end);

    const DateRange = {
      startDate: start,
      endDate: end,
    };

    // add a condition to check if the new and old date range are same
    if (
      this.graphStateList[graphIdx].dateRange.startDate ===
        DateRange.startDate &&
      this.graphStateList[graphIdx].dateRange.endDate === DateRange.endDate
    ) {
      return;
    }
    this.graphStateList[graphIdx].dateRange = DateRange;
    this.graphStateList[graphIdx].configList = null;

    const wasLoaded = this.graphStateList[graphIdx].status === 'loaded';

    if (graphIdx === 0 || wasLoaded) {
      this.getGraphData(
        DateRange,
        graphIdx,
        this.graphStateList[graphIdx].parameterSelections,
      );
    }
  }

  addNewCustomGraph(): void {
    const newDateRange = {
      startDate: '',
      endDate: '',
    };
    this.graphStateList = [
      ...this.graphStateList,
      {
        status: 'idle',
        dateRange: newDateRange,
        parameterSelections: this.parameterOptions.slice(),
        configList: null,
      },
    ];
  }

  removeGraph(graphIndex: number): void {
    if (graphIndex === 0) {
      return;
    }
    this.graphStateList = this.graphStateList.filter(
      (_, idx) => idx !== graphIndex,
    );
  }

  loadCustomGraph(graphIndex: number): void {
    if (!this.canLoadGraph(graphIndex)) {
      return;
    }
    const range = this.graphStateList[graphIndex].dateRange;
    this.getGraphData(
      range,
      graphIndex,
      this.graphStateList[graphIndex].parameterSelections,
    );
  }

  canLoadGraph(graphIndex: number): boolean {
    const range = this.graphStateList[graphIndex].dateRange;
    const parameters = this.graphStateList[graphIndex].parameterSelections;
    return (
      !!range?.startDate &&
      !!range?.endDate &&
      Array.isArray(parameters) &&
      parameters.length > 0 &&
      !(this.graphStateList[graphIndex]?.status === 'loading')
    );
  }

  onParameterSelectionChange(
    graphIndex: number,
    selectedValues: string[],
  ): void {
    this.graphStateList[graphIndex].parameterSelections = selectedValues;
    if (graphIndex === 0) {
      const range = this.graphStateList[graphIndex].dateRange;
      if (range.startDate && range.endDate) {
        this.getGraphData(range, graphIndex, selectedValues);
      }
    } else {
      this.graphStateList[graphIndex].configList = null;
    }
  }

  hasGraphData(config: CoolingChartConfig | null): boolean {
    return !!config?.series && config.series.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getGraphConfigSettings(
    response: CoolingKeyValueData,
    selectedParameters?: string[],
  ): CoolingChartConfig {
    return {
      yAxes: this.getYAxesConfig(response.yAxesGraphData),
      series: this.getSeriesConfig(
        response.measurementParameters,
        selectedParameters,
      ),
    };
  }

  private getYAxesConfig(yAxesGraphData: any): ChartYAxisConfig[] {
    return [
      {
        type: 'value',
        name: 'WaterFlow',
        min: yAxesGraphData['l/h'].min,
        max: yAxesGraphData['l/h'].max,
        interval: yAxesGraphData['l/h'].interval,
        axisLabel: {
          formatter: '{value} l/h',
        },
        position: 'left',
      },
      {
        type: 'value',
        name: 'Temperature',
        min: yAxesGraphData['deg'].min,
        max: yAxesGraphData['deg'].max,
        interval: yAxesGraphData['deg'].interval,
        position: 'right',
        alignTicks: true,
        axisLabel: {
          formatter: '{value} °C',
        },
      },
    ];
  }

  private getSeriesConfig(
    measurementParameters: any,
    selectedParameters?: string[],
  ): ChartSeriesConfig[] {
    const series: ChartSeriesConfig[] = [];
    const availableKeys = selectedParameters?.length
      ? selectedParameters
      : Object.keys(measurementParameters);
    for (const key of availableKeys) {
      if (!measurementParameters[key] || !PARAMETER_CONFIG[key]) {
        continue;
      }
      const parameterConfig = {
        ...PARAMETER_CONFIG[key],
        data: measurementParameters[key],
      };
      series.push(parameterConfig);
    }
    return series;
  }

  //---- system status ----
  getSystemStatus(pastHours: number): void {
    this.isSystemStatusLoading = true;
    const requestBody = {
      serialNumber: this.systemDetails.systemSerialNumber,
      materialNumber: this.systemDetails.systemMaterialNumber,
      pastHours,
    };

    this.coolingCompanionService.getsystemStatusData(requestBody).subscribe({
      next: (systemStatusCollection: SystemStatusCollection) => {
        this.systemStatus = systemStatusCollection.SystemStatusList;
        this.latestStatusDate = systemStatusCollection?.latestDate ?? '';
        this.timezoneOffsetInHours = this.calculateOffsetInHours(
          systemStatusCollection?.timeZoneOffset ?? 0,
        );
        this.isSystemStatusLoading = false;
      },
      error: () => (this.isSystemStatusLoading = false),
    });
  }

  calculateOffsetInHours(offset: number): string {
    const totalMinutes = offset / 60;
    const sign = offset >= 0 ? '+' : '-';

    const absMinutes = Math.abs(totalMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = Math.floor(absMinutes % 60);

    const formatted = `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;

    return `GMT ${sign}${formatted}`;
  }
}
